import {
    isPrepaidContactField,
    sanitizeContactsInput,
    formatContactsToBuyDisplay as computeContactsToBuyDisplay,
    formatContactsCountDisplay as computeContactsCountDisplay,
    computeContactsToBuyDigits,
    computeCampaignContactCost,
    multiplyContactsByUnitPrice,
    addMoneyAmounts,
    divideMoneyAmount,
    formatLargePrice,
} from '@/util/contactLimit'

const LARGE_INPUT_DIGIT_THRESHOLD = 16

function campaignPeriodMultiplier(vm) {
    if (
        vm.selectsPaymentTerm
        && vm.selectsPaymentTerm.PaymentTermSelect === 'Prepaid'
        && vm.prepaidType === 'continual'
        && !vm.contiDurationSelection
    ) {
        return 1
    }
    return parseInt(vm.t_freq || '1', 10)
}

function isMonthlyLeadLimit(vm) {
    return vm.selectsAppModule && vm.selectsAppModule.LeadsLimitSelect === 'month'
}

function digitLength(rawValue) {
    return sanitizeContactsInput(rawValue).length
}

function hasLargeCampaignInputs(vm) {
    return digitLength(vm.LeadspeekLimitLead) >= LARGE_INPUT_DIGIT_THRESHOLD
        || digitLength(vm.LeadspeekCostperlead) >= LARGE_INPUT_DIGIT_THRESHOLD
        || digitLength(vm.t_LeadspeekCostperlead) >= LARGE_INPUT_DIGIT_THRESHOLD
}

export default {
    methods: {
        formatContactsToBuyDisplay(contactsPerDay, isMonthlyBilling) {
            return computeContactsToBuyDisplay(contactsPerDay, isMonthlyBilling)
        },
        formatContactsCountDisplay(rawValue) {
            return computeContactsCountDisplay(rawValue)
        },
        handlePrepaidContactBlur(type, field) {
            if (!isPrepaidContactField(field)) {
                return false
            }

            this[field] = sanitizeContactsInput(this[field])

            if (field.endsWith('Perday') && (this[field] === '' || this[field] === '0')) {
                this[field] = '1'
            }

            if ((field === 'EnhanceLeadsPerday' || field === 'B2bLeadsPerday') && typeof this.validateMinLead === 'function') {
                this.validateMinLead(field)
            }

            this.set_fee(type, field)
            return true
        },
        formatCampaignPrice(value) {
            return formatLargePrice(value)
        },
        fixContinualTopUpIfNeeded() {
            if (
                /e/i.test(String(this.totalLeads.continualTopUp))
                || digitLength(this.LeadspeekLimitLead) >= LARGE_INPUT_DIGIT_THRESHOLD
            ) {
                if (isMonthlyLeadLimit(this)) {
                    this.totalLeads.continualTopUp = sanitizeContactsInput(this.LeadspeekLimitLead) || '0'
                } else {
                    this.totalLeads.continualTopUp = computeContactsToBuyDigits(
                        this.LeadspeekLimitLead,
                        this.contiDurationSelection
                    )
                }
            }
        },
        applyLargeContactProfitTotals(period) {
            let agencyCost
            let clientCost

            if (isMonthlyLeadLimit(this)) {
                agencyCost = multiplyContactsByUnitPrice(this.LeadspeekLimitLead, this.t_LeadspeekCostperlead)
                clientCost = multiplyContactsByUnitPrice(this.LeadspeekLimitLead, this.LeadspeekCostperlead)
            } else {
                agencyCost = computeCampaignContactCost(
                    this.LeadspeekLimitLead,
                    this.t_LeadspeekCostperlead,
                    period
                )
                clientCost = computeCampaignContactCost(
                    this.LeadspeekLimitLead,
                    this.LeadspeekCostperlead,
                    period
                )
            }

            this.t_cost = addMoneyAmounts(this.t_LeadspeekMinCostMonth, clientCost)
            this.t_estleadspermonth = agencyCost
            this.t_profit = addMoneyAmounts(this.t_LeadspeekMinCostMonth, agencyCost)

            if (this.selectsPaymentTerm && this.selectsPaymentTerm.PaymentTermSelect === 'Prepaid' && this.contiDurationSelection) {
                this.t_profit = addMoneyAmounts(this.t_LeadspeekPlatformFee, agencyCost)
            } else if (this.selectsPaymentTerm && this.selectsPaymentTerm.PaymentTermSelect === 'Prepaid' && !this.contiDurationSelection) {
                this.t_profit = addMoneyAmounts(this.t_LeadspeekPlatformFee, divideMoneyAmount(agencyCost, 4))
            }
        },
        shouldUseLargeContactProfitTotals(estimateValue) {
            return hasLargeCampaignInputs(this) || /e/i.test(String(estimateValue))
        },
        recoverUnsafeFormatPrice(value) {
            const freq = parseInt(this.t_freq || '1', 10)
            const weekCost = computeCampaignContactCost(this.LeadspeekLimitLead, this.LeadspeekCostperlead, 1)
            const freqCost = computeCampaignContactCost(this.LeadspeekLimitLead, this.LeadspeekCostperlead, freq)

            if (isMonthlyLeadLimit(this)) {
                if (digitLength(this.t_LeadspeekCostperlead) >= LARGE_INPUT_DIGIT_THRESHOLD
                    && digitLength(this.LeadspeekCostperlead) < LARGE_INPUT_DIGIT_THRESHOLD) {
                    return multiplyContactsByUnitPrice(this.LeadspeekLimitLead, this.t_LeadspeekCostperlead)
                }
                return multiplyContactsByUnitPrice(this.LeadspeekLimitLead, this.LeadspeekCostperlead)
            }

            if (!Number.isFinite(Number(value))) {
                if (digitLength(this.LeadspeekPlatformFee) >= LARGE_INPUT_DIGIT_THRESHOLD) {
                    return addMoneyAmounts(
                        computeCampaignContactCost(
                            this.LeadspeekLimitLead,
                            this.LeadspeekCostperlead,
                            campaignPeriodMultiplier(this)
                        ),
                        this.LeadspeekPlatformFee
                    )
                }
                if (digitLength(this.LeadspeekMinCostMonth) >= LARGE_INPUT_DIGIT_THRESHOLD) {
                    return addMoneyAmounts(freqCost, this.LeadspeekMinCostMonth)
                }
                if (this.totalLeads && this.totalLeads.oneTime) {
                    if (digitLength(this.t_LeadspeekPlatformFee) >= LARGE_INPUT_DIGIT_THRESHOLD) {
                        return addMoneyAmounts(
                            multiplyContactsByUnitPrice(this.totalLeads.oneTime, this.t_LeadspeekCostperlead),
                            this.t_LeadspeekPlatformFee
                        )
                    }
                    return multiplyContactsByUnitPrice(this.totalLeads.oneTime, this.LeadspeekCostperlead)
                }
                if (digitLength(this.t_LeadspeekCostperlead) >= LARGE_INPUT_DIGIT_THRESHOLD && this.totalLeads && this.totalLeads.oneTime) {
                    return multiplyContactsByUnitPrice(this.totalLeads.oneTime, this.t_LeadspeekCostperlead)
                }
            }

            return this.contiDurationSelection ? freqCost : weekCost
        },
        recoverAgencyWeeklyProfitDisplay() {
            return divideMoneyAmount(this.t_estleadspermonth, 4)
        },
    },
}
